import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import type { ReactNode } from 'react';
import RenderInBody from './RenderInBody';
import './CorgeeModal.css';

export interface CorgeeModalRef {
  openModal: (callback?: () => void) => void;
  closeModal: () => void;
}

interface CorgeeModalProps {
  onClose?: () => boolean | void;
  onSave?: () => void;
  className?: string;
  title?: string;
  headerExtra?: ReactNode;
  actionButtons?: any[];
  isDraggable?: boolean;
  isDuplicateProject?: boolean;
  closeOnEscape?: boolean;
  textButton?: string;
  cancelText?: string;
  height?: number;
  width?: number | string;
  bodyClassName?: string;
  saveButtonColor?: string;
  saveButtonDisabled?: boolean;
  isSaving?: boolean;
  allowSaveAndNewCheckBox?: boolean;
  callBackFunction?: (checked: boolean) => void;
  openModal?: boolean;
  hideActionButtons?: boolean;
  allowMinimize?: boolean;
  allowMaximize?: boolean;
  hasMaxHeight?: boolean;
  transparentBackdrop?: boolean;
  editDuplicateCheck?: () => void;
  children?: ReactNode;
  // Extra props forwarded from callers
  [key: string]: any;
}

const CorgeeModal = forwardRef<CorgeeModalRef, CorgeeModalProps>(
  (
    {
      onClose,
      onSave,
      className = '',
      title,
      headerExtra,
      isDraggable = false,
      isDuplicateProject = false,
      closeOnEscape: _closeOnEscape = false,
      textButton,
      cancelText = 'Cancel',
      width = 500,
      saveButtonColor,
      saveButtonDisabled = false,
      isSaving = false,
      allowSaveAndNewCheckBox = false,
      callBackFunction,
      openModal = false,
      hideActionButtons = false,
      allowMinimize: _allowMinimize = false,
      allowMaximize: _allowMaximize = false,
      hasMaxHeight = false,
      transparentBackdrop = false,
      editDuplicateCheck,
      children,
    },
    ref,
  ) =>
  {
    const [isOpen, setIsOpen] = useState<boolean>(openModal);
    const openCallbackRef = useRef<(() => void) | undefined>(undefined);
    const [duplicateCheckbox, setDuplicateCheckbox] = useState<boolean>(true);
    const [saveAndNew, setSaveAndNew] = useState<boolean>(false);
    const modalRef = useRef<HTMLDivElement>(null);
    const [dragPos, setDragPos] = useState<{ x: number; y: number } | null>(null);
    const dragHandlersRef = useRef<{ onMove: (e: MouseEvent) => void; onUp: () => void } | null>(null);

    useImperativeHandle(ref, () => ({
      openModal: (callback?: () => void) =>
      {
        openCallbackRef.current = callback;
        setSaveAndNew(false);
        setIsOpen(true);
      },
      closeModal: () =>
      {
        setIsOpen(false);
      },
    }));

    useEffect(() =>
    {
      if (isOpen && openCallbackRef.current)
      {
        const cb = openCallbackRef.current;
        openCallbackRef.current = undefined;
        cb();
        setDuplicateCheckbox(duplicateCheckbox);
      }
    }, [isOpen]);

    useEffect(() =>
    {
      setIsOpen(openModal);
    }, [openModal]);

    useEffect(() =>
    {
      if (!isDraggable) return;
      if (isOpen)
      {
        requestAnimationFrame(() =>
        {
          const el = modalRef.current;
          if (!el) return;
          const rect = el.getBoundingClientRect();
          setDragPos({
            x: (window.innerWidth - rect.width) / 2,
            y: Math.max(24, (window.innerHeight - rect.height) / 2 - 100),
          });
        });
      }
      else
      {
        setDragPos(null);
      }
    }, [isOpen, isDraggable]);

    useEffect(() =>
    {
      if (!isDraggable) return;
      const handleResize = (): void =>
      {
        setDragPos(prev =>
        {
          if (!prev || !modalRef.current) return prev;
          const maxX = window.innerWidth - modalRef.current.offsetWidth;
          const maxY = window.innerHeight - modalRef.current.offsetHeight;
          return { x: Math.max(0, Math.min(prev.x, maxX)), y: Math.max(0, Math.min(prev.y, maxY)) };
        });
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [isDraggable]);

    useEffect(() =>
    {
      return () =>
      {
        if (dragHandlersRef.current)
        {
          document.removeEventListener('mousemove', dragHandlersRef.current.onMove);
          document.removeEventListener('mouseup', dragHandlersRef.current.onUp);
          dragHandlersRef.current = null;
        }
        document.body.classList.remove('corgee-modal-dragging');
      };
    }, []);

    const handleClose = (): void =>
    {
      let validateToClose = true;

      if (onClose && typeof onClose === 'function')
      {
        validateToClose = onClose();
      }
      if (validateToClose == undefined) setIsOpen(false);
    };

    const handleSave = (): void =>
    {
      if (saveButtonDisabled || isSaving) return;

      if (onSave && typeof onSave === 'function')
      {
        onSave();
      }
    };

    const getSaveButtonClassName = (): string =>
    {
      return saveButtonColor !== undefined && saveButtonColor !== ''
        ? 'corgee_com_button'
        : 'corgee_com_button corgee_com_button-disabled';
    };

    const getSaveButtonStyle = (): React.CSSProperties =>
    {
      if (saveButtonColor !== undefined && saveButtonColor !== '')
      {
        return {
          borderColor: saveButtonColor,
          backgroundColor: saveButtonColor,
        };
      }
      return {};
    };

    const handleDragMouseDown = (e: React.MouseEvent<HTMLDivElement>): void =>
    {
      if (!isDraggable || !dragPos) return;
      if ((e.target as HTMLElement).closest('.corgee_com_global-modal_header-close-btn')) return;
      e.preventDefault();

      const originX = dragPos.x;
      const originY = dragPos.y;
      const startX = e.clientX;
      const startY = e.clientY;

      const onMove = (ev: MouseEvent): void =>
      {
        const el = modalRef.current;
        const newX = originX + (ev.clientX - startX);
        const newY = originY + (ev.clientY - startY);
        if (el)
        {
          setDragPos({
            x: Math.max(0, Math.min(newX, window.innerWidth - el.offsetWidth)),
            y: Math.max(0, Math.min(newY, window.innerHeight - el.offsetHeight)),
          });
        }
        else
        {
          setDragPos({ x: newX, y: newY });
        }
      };

      const onUp = (): void =>
      {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.classList.remove('corgee-modal-dragging');
        dragHandlersRef.current = null;
      };

      dragHandlersRef.current = { onMove, onUp };
      document.body.classList.add('corgee-modal-dragging');
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    };

    const onCheckedDuplicateCards = (e: React.ChangeEvent<HTMLInputElement>) =>
    {
      editDuplicateCheck(e.target.checked);
      setDuplicateCheckbox(e.target.checked);
    };

    return (
      <div>
        {isOpen && (
          <RenderInBody>
            <div
              className={`corgee_com_modal-backdrop ${transparentBackdrop ? 'transparent' : ''} ${isDraggable ? 'draggable-mode' : ''}`}
            >
              <div
                ref={modalRef}
                className={`corgee_com_conversation-metadata-modal _DEL_corgee-modal-container ${className}`}
                style={{
                  width: typeof width === 'number' ? `${width}px` : width,
                  ...(isDraggable
                    ? dragPos
                      ? {
                        position: 'fixed',
                        left: `${dragPos.x}px`,
                        top: `${dragPos.y}px`,
                      }
                      : {
                        position: 'fixed',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                      }
                    : {}),
                }}
              >
                <div
                  className={`corgee_com_global-modal_header${isDraggable ? ' draggable-handle' : ''}`}
                  onMouseDown={isDraggable ? handleDragMouseDown : undefined}
                >
                  <div className="header-info">
                    <div className="react-add-new-card-title" id="contained-modal-title-sm">
                      {title}
                    </div>

                    <button
                      className="corgee_com_global-modal_header-close-btn _DEL_react-corgee-wizard_close-control-btn"
                      onClick={handleClose}
                    >
                      <i className="icon-incorrect-icon"></i>
                    </button>
                  </div>
                </div>
                {headerExtra && (
                  <div className="corgee_com_global-modal_header-extra">{headerExtra}</div>
                )}
                <div
                  className={
                    hasMaxHeight
                      ? 'corgee_com_global-modal_body modal_body-max-height'
                      : 'corgee_com_global-modal_body'
                  }
                >
                  {children}
                </div>

                <div className="corgee_com_global-modal_footer _DEL_react-corgee-wizard_modal-footer">
                  {!hideActionButtons && (
                    <div className={isDuplicateProject ? 'duplicate-project' : ''}>
                      {allowSaveAndNewCheckBox && (
                        <div className="corgee_com_global-modal_save-and-new">
                          <span className="corgee_com_checkbox _DEL_custom-checkbox">
                            <input
                              id="createAnotherItem"
                              type="checkbox"
                              checked={saveAndNew}
                              onChange={e =>
                              {
                                setSaveAndNew(e.target.checked);
                                if (callBackFunction) callBackFunction(e.target.checked);
                              }}
                            />
                            <label htmlFor="createAnotherItem" style={{ top: '-2px' }}></label>
                          </span>
                          <label
                            htmlFor="createAnotherItem"
                            className="corgee_com_global-modal_save-and-new-title _DEL_add-another-item"
                          >
                            Save and New
                          </label>
                        </div>
                      )}
                      {isDuplicateProject && (
                        <span className="custom-checkbox">
                          <input
                            id="createAnotherItem"
                            type="checkbox"
                            value=""
                            onClick={onCheckedDuplicateCards as any}
                            className="ng-pristine ng-untouched ng-valid"
                            checked={duplicateCheckbox}
                          />
                          <label htmlFor="createAnotherItem" className="add-another-item">
                            Duplicate Cards on new project.
                          </label>
                        </span>
                      )}

                      <div>
                        <button
                          type="button"
                          className="corgee_com_button corgee_com_button-cancel _DEL_react-corgee-wizard_cancel-btn"
                          onClick={handleClose}
                        >
                          {cancelText}
                        </button>

                        <button
                          type="button"
                          className={getSaveButtonClassName()}
                          style={getSaveButtonStyle()}
                          onClick={handleSave}
                          disabled={saveButtonDisabled || isSaving}
                        >
                          {isSaving && <span className="corgee_com_button-spinner" />}
                          {textButton && textButton.length > 0 ? textButton : 'Save'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </RenderInBody>
        )}
      </div>
    );
  },
);

CorgeeModal.displayName = 'CorgeeModal';

export default CorgeeModal;
